import { FunctionComponent, useContext, useEffect, useState } from 'react'
import styled, { ThemeContext } from 'styled-components'

import ErrorBox from 'components/ErrorBox'
import SelectInput from 'components/GlobalStyles/Inputs/SelectInput'
import { getFormBuilderSelectInputStyles } from 'components/GlobalStyles/Inputs/utils'
import Spacer from 'components/Spacer'

import { capitalizeWord } from 'utils/stringFormatting'

const FlexRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`

const InputContainer = styled.div`
  flex: 1;
`

export interface IRange {
  min?: number
  max?: number
}

export interface IRangeOption {
  label: string
  value: number | undefined
}

export interface IRangeOptionSelection {
  min?: IRangeOption | null
  max?: IRangeOption | null
}

export const getRangeOptionFromValue = (
  range: IRange,
  minOptions: IRangeOption[],
  maxOptions: IRangeOption[],
) => {
  const minOption = minOptions.find(option => option?.value === range?.min) ?? null
  const maxOption = maxOptions.find(option => option?.value === range?.max) ?? null

  return {
    min: minOption,
    max: maxOption,
  }
}

export const areRangeValuesEqual = (range: IRange, rangeOptionSelection: IRangeOptionSelection) =>
  range?.min === rangeOptionSelection.min?.value && range?.max === rangeOptionSelection.max?.value

interface IProps {
  label: string
  minOptions: IRangeOption[]
  maxOptions: IRangeOption[]
  minPlaceholder?: string
  maxPlaceholder?: string
  selectedRangeOption: IRangeOptionSelection
  setSelectedRangeOption: (range: IRangeOptionSelection) => void
  onError?: (error: string) => void
}

const RangeInput: FunctionComponent<IProps> = ({
  label,
  minOptions,
  maxOptions,
  minPlaceholder,
  maxPlaceholder,
  selectedRangeOption,
  setSelectedRangeOption,
  onError,
}) => {
  const theme = useContext(ThemeContext)

  const [error, setError] = useState<string>('')

  useEffect(() => {
    if (onError) onError(error)
  }, [error])

  const handleSelection = (selection: IRangeOption, type: 'min' | 'max') => {
    setError('')

    const errorOperator = type === 'min' ? 'greater than' : 'less than'
    const otherType = type === 'min' ? 'max' : 'min'

    const mainType = capitalizeWord(type)
    const errorMessage = `${mainType} ${label}s must be ${errorOperator} ${otherType} ${label}s`

    if (
      type === 'min'
      && selectedRangeOption.max?.value
      && selection.value
      && selection.value > selectedRangeOption.max.value
    ) {
      setError(errorMessage)
      setSelectedRangeOption({ ...selectedRangeOption, [type]: null })
      return
    }

    if (
      type === 'max'
      && selectedRangeOption.min?.value
      && selection.value
      && selection.value < selectedRangeOption.min.value
    ) {
      setError(errorMessage)
      setSelectedRangeOption({ ...selectedRangeOption, [type]: null })
      return
    }

    setSelectedRangeOption({ ...selectedRangeOption, [type]: selection })
  }

  const handleSelectMin = (selection: IRangeOption) => handleSelection(selection, 'min')

  const handleSelectMax = (selection: IRangeOption) => handleSelection(selection, 'max')

  // select input custom styling
  const stylesConfig = getFormBuilderSelectInputStyles({ theme })

  // this is necessary if this is in a dropdown menu
  const stopPropagation = (e: React.MouseEvent) => e.stopPropagation()

  return (
    <>
      <FlexRow>
        <InputContainer onClick={stopPropagation}>
          <SelectInput
            placeholder={minPlaceholder || 'No min'}
            value={selectedRangeOption.min}
            options={minOptions}
            onChange={handleSelectMin}
            stylesConfig={stylesConfig}
          />
        </InputContainer>
        <Spacer size={theme.spacings[8]} />
        -
        <Spacer size={theme.spacings[8]} />
        <InputContainer onClick={stopPropagation}>
          <SelectInput
            placeholder={maxPlaceholder || 'No max'}
            value={selectedRangeOption.max}
            options={maxOptions}
            onChange={handleSelectMax}
            stylesConfig={stylesConfig}
          />
        </InputContainer>
      </FlexRow>
      {error && (
        <>
          <Spacer size={theme.spacings[8]} />
          <ErrorBox error={error} />
        </>
      )}
    </>
  )
}

export default RangeInput
